import { useMemo } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { BsFillTrashFill } from 'react-icons/bs';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { ButtonSplitGroup } from '@components/Buttons';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { BootstrapTable } from '@components/Table/Table';
import { Page500 } from '@containers/ErrorPages';
import { checkIsReady } from '@features/queryUtils';
import { cartApi } from '@reducers/order';

const renderActionCell = ({ row }) => {
  const queryFunc = row.original.is_backorder
    ? cartApi.useRemoveBackorderFromCartMutation
    : cartApi.useRemoveDidFromCartMutation;
  const [trigger, result] = queryFunc();
  const { id: planID, type, prefixID } = useParams();
  return (
    <Button
      variant="didxl"
      onClick={() => {
        trigger({ id: row.values.id, prefixID, planID, type });
      }}
      size="sm"
      disabled={result.isLoading}
    >
      <BsFillTrashFill size={17} />
    </Button>
  );
};

const SelectedNumbersTable = ({
  useData,
  useDataArgs,
  header = 'Selected DIDs for Plan',
  backorder = false,
}) => {
  const [trigger, result] = cartApi.useCancelDidsFromPlanCartMutation();
  const [cancelBackorders] = cartApi.useCancelBackordersFromPlanCartMutation();
  const navigate = useNavigate();
  const { id, type, prefixID } = useParams();
  const backorderColumns = backorder
    ? [
        {
          Header: 'Amount',
          accessor: 'amount',
          className: 'col-md-1 text-center',
        },
      ]
    : [];
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
        className: 'hidden',
      },
      {
        Header: 'Number',
        accessor: 'did',
        className: 'col-md-7',
      },
      ...backorderColumns,
      {
        Header: 'Action',
        Cell: ({ row }) => renderActionCell({ row }),
        accessor: 'action',
        className: 'col-md-4 text-center',
      },
    ],
    []
  );
  const cart = useData({ ...useDataArgs });
  const isLoading = cart.isLoading || cart.isFetching;
  const isReady = checkIsReady(cart);
  return (
    <Card>
      <Card.Header className="bg-didxl text-white">{header}</Card.Header>
      <Card.Body>
        {isLoading ? (
          <Row>
            <DefaultSpinnerCenter />
          </Row>
        ) : null}
        {cart.isError ? <Page500 /> : null}
        {isReady ? (
          <>
            <Row>
              <Col>
                <BootstrapTable
                  columns={columns}
                  data={cart.data}
                  tableProps={() => ({
                    responsive: true,
                    bordered: true,
                    className: 'normalFontSize',
                  })}
                  hiddenColumns="id"
                  disabled={result.isLoading}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <ButtonSplitGroup>
                  <Button
                    onClick={() => {
                      cancelBackorders({ id, type, prefixID });
                      trigger({ id, type, prefixID })
                        .unwrap()
                        .then(() => {
                          navigate('/order/numbers');
                        });
                    }}
                    variant="didxl"
                    size="sm"
                  >
                    Cancel
                  </Button>
                  {cart.data.length > 0 ? (
                    <Button
                      as={Link}
                      to={`/order/numbers/plans/${id}/type/${type}/prefixes/${prefixID}/checkout`}
                      variant="didxl"
                      size="sm"
                      className="me-2"
                    >
                      Continue
                    </Button>
                  ) : null}
                </ButtonSplitGroup>
              </Col>
            </Row>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default SelectedNumbersTable;
